import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Sprite from './Sprite';
import { useDexControls } from './dexControls';
import { getMetaFormat, getMetaOverview } from '../services/pokemonService';
import { artworkUrl, formatUsage } from '../utils/dex';
import { toQuery } from '../utils/pokemon';

function entryPath(row) {
  if (row.dexSlug) return `/pokemon/${row.dexSlug}`;
  if (row.dexNumber) return `/pokemon/${row.dexNumber}`;
  return null;
}

function comparePath(row) {
  const query = toQuery(row.name) || (row.dexNumber ? String(row.dexNumber) : '');
  return query ? `/compare?with=${encodeURIComponent(query)}` : '/compare';
}

function formatShare(percent) {
  if (typeof percent !== 'number') return '—';
  if (percent <= 0) return '<0.1%';
  return formatUsage(percent);
}

function ShareList({ title, items }) {
  if (!items?.length) return null;
  return (
    <div>
      <h4>{title}</h4>
      <ul>
        {items.map((item) => (
          <li key={`${title}-${item.name}`}>
            {item.name}
            <span> {formatShare(item.percent)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function UsageRow({ row, selected, onSelect, rowRef, ladder, showAgents }) {
  const path = entryPath(row);
  const agentPath = path ? `${path}/agents` : null;
  return (
    <li
      className={selected ? 'meta-row is-selected' : 'meta-row'}
      ref={selected ? rowRef : undefined}
    >
      <button className="meta-select" type="button" aria-pressed={selected} onClick={onSelect}>
        <span className="rank"><span className="sr-only">Rank </span>{row.rank}</span>
        <Sprite
          src={row.sprite}
          fallback={artworkUrl(row.dexNumber)}
          alt=""
        />
        <span className="meta-copy">
          <span className="meta-name">{row.name}</span>
          <span className="meta-facts">
            <span className="usage">{formatUsage(row.usage)} usage</span>
            <span className="tier-badge">{row.tier || `${ladder} ladder`}</span>
            {selected && <span className="selected-flag">Selected</span>}
          </span>
        </span>
      </button>
      <span className="meta-links">
        {path ? <Link to={path}>Entry</Link> : <span className="muted">No entry</span>}
        {showAgents && agentPath ? <Link to={agentPath}>Agents</Link> : null}
        <Link to={comparePath(row)}>Compare</Link>
      </span>
    </li>
  );
}

function MetaTiers() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [detail, setDetail] = useState(null);
  const [formatId, setFormatId] = useState('overview');
  const [style, setStyle] = useState('all');
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(0);
  const selectedRef = useRef(null);

  const loadOverview = (fresh = false) => {
    setLoading(true);
    setError('');
    getMetaOverview({ fresh })
      .then((data) => {
        setOverview(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || 'Smogon usage stats are unavailable right now.');
        setLoading(false);
      });
  };

  useEffect(() => {
    loadOverview(false);
  }, []);

  useEffect(() => {
    if (formatId === 'overview') {
      setDetail(null);
      setDetailLoading(false);
      return undefined;
    }
    let active = true;
    setDetailLoading(true);
    setError('');
    getMetaFormat(formatId)
      .then((data) => {
        if (!active) return;
        setDetail(data);
        setDetailLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        setDetail(null);
        setError(err.message || 'Smogon usage stats are unavailable right now.');
        setDetailLoading(false);
      });
    return () => {
      active = false;
    };
  }, [formatId]);

  const tiers = useMemo(() => {
    const list = overview?.tiers || [];
    if (style === 'all') return list;
    return list.filter((tier) => tier.group === style);
  }, [overview, style]);

  const flatRows = useMemo(() => {
    if (formatId !== 'overview') {
      return (detail?.entries || []).map((row) => ({
        ...row,
        ladder: detail?.format?.label || 'Usage',
      }));
    }
    return tiers.flatMap((tier) => (tier.entries || []).map((row) => ({
      ...row,
      ladder: tier.label,
    })));
  }, [formatId, detail, tiers]);

  useEffect(() => {
    setSelected(0);
  }, [formatId, style, detail]);

  useEffect(() => {
    selectedRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [selected]);

  const openSelected = () => {
    const row = flatRows[selected];
    const path = row && entryPath(row);
    if (path) navigate(path);
  };

  useDexControls({
    onUp: () => setSelected((index) => Math.max(0, index - 1)),
    onDown: () => setSelected((index) => Math.min(Math.max(flatRows.length - 1, 0), index + 1)),
    onA: openSelected,
    onB: () => {
      if (formatId !== 'overview') setFormatId('overview');
      else navigate('/');
    },
  });

  const monthLabel = detail?.monthLabel || overview?.monthLabel;
  const sourceUrl = detail?.sourceUrl || overview?.sourceUrl;
  const warning = error || detail?.warning || overview?.warning;
  const formats = overview?.formats || [];
  const groups = ['Singles', 'Doubles', 'Other'];
  const selectedRow = flatRows[selected];

  return (
    <div className="page">
      <div className="page-intro">
        <div>
          <h1>Usage tiers</h1>
          <p className="lede">
            The most-used Pokémon on the current Smogon ladders, grouped by tier.
          </p>
        </div>
      </div>

      <div className="filters">
        <div>
          <label htmlFor="meta-gen">Generation</label>
          <select id="meta-gen" value="9" onChange={() => {}}>
            <option value="9">Generation 9</option>
          </select>
        </div>
        <div>
          <label htmlFor="meta-style">Battle style</label>
          <select
            id="meta-style"
            value={style}
            onChange={(event) => {
              setStyle(event.target.value);
              setFormatId('overview');
            }}
          >
            <option value="all">All styles</option>
            <option value="Singles">Singles</option>
            <option value="Doubles">Doubles</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div>
          <label htmlFor="meta-format">Format</label>
          <select
            id="meta-format"
            value={formatId}
            onChange={(event) => {
              const next = event.target.value;
              setFormatId(next);
              const format = formats.find((item) => item.id === next);
              if (format) setStyle(format.group);
            }}
          >
            <option value="overview">All tiers</option>
            {groups.map((group) => {
              const options = formats.filter((format) => format.group === group);
              if (!options.length) return null;
              return (
                <optgroup key={group} label={group}>
                  {options.map((format) => (
                    <option key={format.id} value={format.id}>{format.label}</option>
                  ))}
                </optgroup>
              );
            })}
          </select>
        </div>
      </div>

      {monthLabel && sourceUrl && (
        <p className="source-line">
          Usage for {monthLabel}. Source{' '}
          <a href={sourceUrl} rel="noreferrer">Smogon stats</a>.
          Tiers from Pokémon Showdown.
          {detail?.format?.file ? ` File ${detail.format.file}.` : ''}
        </p>
      )}

      {loading && <p className="status" role="status">Loading usage stats…</p>}
      {detailLoading && <p className="status" role="status">Loading this format…</p>}

      {warning && (
        <div className="alert" role={error ? 'alert' : 'status'}>
          <p>{warning}</p>
          {error && (
            <button className="btn btn-primary" type="button" onClick={() => loadOverview(true)}>
              Retry
            </button>
          )}
        </div>
      )}

      {!loading && formatId === 'overview' && tiers.map((tier) => (
        <section className="tier-block" key={tier.id} aria-labelledby={`tier-${tier.id}`}>
          <div className="tier-heading">
            <h2 id={`tier-${tier.id}`}>{tier.label}</h2>
            <p>
              {tier.group}
              {tier.battles ? ` · ${tier.battles.toLocaleString('en-US')} battles` : ''}
              {tier.cutoff ? ` · cutoff ${tier.cutoff}` : ''}
            </p>
          </div>
          <ol className="meta-list">
            {(tier.entries || []).map((row) => {
              const index = flatRows.findIndex((item) => item.rank === row.rank && item.name === row.name && item.ladder === tier.label);
              return (
                <UsageRow
                  key={`${tier.id}-${row.rank}-${row.name}`}
                  row={row}
                  ladder={tier.label}
                  selected={index === selected}
                  onSelect={() => setSelected(index)}
                  rowRef={selectedRef}
                  showAgents={Boolean(row.hasAgents)}
                />
              );
            })}
          </ol>
        </section>
      ))}

      {!detailLoading && formatId !== 'overview' && detail && (
        <section className="tier-block" aria-labelledby="format-heading">
          <div className="tier-heading">
            <h2 id="format-heading">{detail.format?.label}</h2>
            <p>
              {detail.format?.group}
              {detail.format?.battles ? ` · ${detail.format.battles.toLocaleString('en-US')} battles` : ''}
              {detail.format?.cutoff ? ` · cutoff ${detail.format.cutoff}` : ''}
            </p>
          </div>
          <ol className="meta-list">
            {detail.entries.map((row, index) => (
              <UsageRow
                key={`${row.rank}-${row.name}`}
                row={row}
                ladder={detail.format?.label || 'Usage'}
                selected={index === selected}
                onSelect={() => setSelected(index)}
                rowRef={selectedRef}
                showAgents={Boolean(row.hasAgents)}
              />
            ))}
          </ol>
        </section>
      )}

      {formatId !== 'overview' && selectedRow && !detailLoading && (
        <section className="set-panel" aria-live="polite" aria-label={`${selectedRow.name} sets`}>
          <h3>{selectedRow.name} sets</h3>
          {selectedRow.moves?.length || selectedRow.items?.length || selectedRow.teammates?.length ? (
            <div className="set-grid">
              <ShareList title="Moves" items={selectedRow.moves} />
              <ShareList title="Items" items={selectedRow.items} />
              <ShareList title="Abilities" items={selectedRow.abilities} />
              <ShareList title="Teammates" items={selectedRow.teammates} />
            </div>
          ) : (
            <p className="muted">Move, item, and teammate details are unavailable for this Pokémon.</p>
          )}
        </section>
      )}
    </div>
  );
}

export default MetaTiers;
